"use client";

import { NAVBAR_LOGO_HEIGHT } from "@/lib/constants";
import { cn } from "@/lib/utils";
import lightModeLogo from "@/public/artefact_dark.svg";
import darkModeLogo from "@/public/artefact_white.svg";
import roseBg from "@/public/rose_bg.jpg";
import { Role } from "@prisma/client";
import { Flex } from "@radix-ui/themes";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Container from "./components/Container";
import AuthStatusButton from "./components/navbar/AuthStatusButton";
import AuctionsTab from "./components/navbar/NavBarAuctionsTab";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "./components/ui/navigation-menu";
import { ExtendedSession } from "@/lib/interface/ExtendedSession";

const whiteNavBar =
  "bg-white focus:bg-slate-200 hover:bg-slate-200 text-black hover:text-black focus:text-black select-none rounded-md";
const blackNavBar =
  "bg-slate-900 focus:bg-slate-900 hover:bg-slate-700 text-white hover:text-white focus:text-white select-none rounded-md";

const darkNavBar =
  "bg-transparent focus:bg-biddifySecondary hover:bg-biddifySecondary text-white hover:text-white focus:text-white select-none rounded-md";

const clearNavBar =
  "bg-transparent focus:bg-transparent hover:bg-transparent text-gray-800 hover:text-black/80 focus:text-black/60 select-none rounded-md";

const NavBar = ({ session }: { session: ExtendedSession | null }) => {
  const pathname = usePathname();
  // Don't show navbar on login page
  if (pathname === "/auth/signin") return null;

  return (
    <nav
      className={cn(
        "border-b px-5 relative",
        session?.role === Role.ADMIN ? "bg-slate-900" :
          "bg-gradient-to-br from-biddify to-biddifySecondary"
      )}
    >
      <Container className="w-full pb-0">
        <Flex justify="between" className="w-full">
          <Flex align="center" style={{ height: NAVBAR_LOGO_HEIGHT }}>
            <Link href={process.env.NEXT_PUBLIC_TENANT_HOMEPAGE ?? '/'}>
              <Image
                // src={session?.role === Role.ADMIN ? darkModeLogo : lightModeLogo}
                src={darkModeLogo}
                alt="Logo"
                height={40}
              />
            </Link>
          </Flex>

          <NavLinks session={session!} />
        </Flex>
      </Container>
    </nav>
  );
};

const NavLinks = ({ session }: { session: ExtendedSession }) => {
  const navButtonStyle =
    session?.role == Role.ADMIN ? blackNavBar : darkNavBar;

  if (!session)
    return (
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem
            className={cn(navigationMenuTriggerStyle(), navButtonStyle)}
          >
            <NavigationMenuLink href={"/"}>Home</NavigationMenuLink>
          </NavigationMenuItem>
          <AuctionsTab navButtonStyle={navButtonStyle} />
          <NavigationMenuItem
            className={cn(navigationMenuTriggerStyle(), navButtonStyle)}
          >
            <NavigationMenuLink href={"/api/auth/signin"}>
              Log in
            </NavigationMenuLink>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
    );

  if (!session) return null;

  // Logged in.
  const links =
    session?.role === Role.BUYER
      ? [
        { label: "Home", href: "/" },
        // { label: "About Us", href: "/about" },
        {
          label: "Auctions",
          href: "/",
          component: () => <AuctionsTab navButtonStyle={navButtonStyle} />,
        },
        {
          label: "Account",
          href: "/account/details",
          component: () => (
            <AuthStatusButton
              session={session}
              navButtonStyle={navButtonStyle}
            />
          ),
        },
      ]
      : [
        { label: "Home", href: "/" },
        { label: "Auctions", href: "/admin/auctions" },
        {
          label: "Account",
          href: "/account/details",
          component: () => (
            <AuthStatusButton
              session={session}
              navButtonStyle={navButtonStyle}
            />
          ),
        },
      ];

  return (
    <NavigationMenu>
      <NavigationMenuList>
        {links.map((link, index) => {
          if (link.component) return <link.component key={index} />;
          return (
            <NavigationMenuItem key={link.href} className={navButtonStyle}>
              <Link href={link?.href ?? '/'} legacyBehavior passHref>
                <NavigationMenuLink
                  className={cn(
                    navigationMenuTriggerStyle(),
                    navButtonStyle
                  )}
                >
                  {link.label}
                </NavigationMenuLink>
              </Link>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default NavBar;
